/**
 * Roles this organisation has defined.
 *
 *   ┌ Roles ──────────────────────────────────── Total (N) ┐
 *   │ [↻] [Search] [+ New role]    │ Edit  Delete          │
 *   │ Name        Based on   Can reach        Batches  Who │
 *   └──────────────────────────────────────────────────────┘
 *
 * A role is built on one of the five built-in roles and takes things away from it: the
 * modules it may reach and, optionally, the batches it is limited to. A tutor is an
 * instructor with Student diary and Attendance for one batch, and nothing else.
 *
 * The base role is fixed once created — changing it would leave everybody holding the
 * role with permissions that no longer match what they were given.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import DataGrid from '../../components/datagrid/DataGrid';
import SplitLayout, { paneForm } from '../../components/shell/SidePane';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, PencilSimple, Trash, IdentificationBadge, UsersThree } from '@phosphor-icons/react';
import { ROLE_LABELS } from '../../components/shell/moduleRegistry';

const BLANK = { name: '', description: '', base_role: 'instructor', modules: [], batch_ids: [], is_active: true };

export default function RoleManagement() {
  const { api } = useAuth();
  const [roles, setRoles] = useState([]);
  const [options, setOptions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);     // null = pane closed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o, b] = await Promise.all([
        api.get('/roles'),
        api.get('/roles/options'),
        api.get('/batches').catch(() => ({ data: [] })),
      ]);
      setRoles(r.data || []);
      setOptions(o.data?.base_roles || []);
      setBatches(b.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load roles');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const moduleLabels = useMemo(() => {
    const out = {};
    options.forEach((b) => b.modules.forEach((m) => { out[m.key] = m.label; }));
    return out;
  }, [options]);

  const offered = useMemo(
    () => options.find((b) => b.key === (editing?.base_role || 'instructor'))?.modules || [],
    [options, editing?.base_role],
  );

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        name: editing.name, description: editing.description, base_role: editing.base_role,
        modules: editing.modules, batch_ids: editing.batch_ids, is_active: true,
      };
      if (editing.id) await api.put(`/roles/${editing.id}`, body);
      else await api.post('/roles', body);
      toast.success(editing.id ? 'Role updated' : 'Role created');
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the role');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (role) => {
    const held = role.user_count
      ? `\n\n${role.user_count} user${role.user_count === 1 ? '' : 's'} hold this role and will go back to their plain ${ROLE_LABELS[role.base_role] || role.base_role} role.`
      : '';
    if (!window.confirm(`Delete the role "${role.name}"?${held}`)) return;
    try {
      const res = await api.delete(`/roles/${role.id}`);
      toast.success(res.data?.message || 'Role deleted');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete the role');
    }
  };

  const toggleModule = (key) => setEditing((r) => ({
    ...r,
    modules: r.modules.includes(key) ? r.modules.filter((k) => k !== key) : [...r.modules, key],
  }));
  const toggleBatch = (id) => setEditing((r) => ({
    ...r,
    batch_ids: r.batch_ids.includes(id) ? r.batch_ids.filter((b) => b !== id) : [...r.batch_ids, id],
  }));

  let side = null;
  if (editing) {
    const f = paneForm({
      id: 'role-form', onSubmit: save, onCancel: () => setEditing(null),
      submitLabel: editing.id ? 'Save role' : 'Create role', submitting,
      children: (
        <>
          <div className="field">
            <Label htmlFor="role-name">Name *</Label>
            <Input id="role-name" required value={editing.name} placeholder="Tutor"
              onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="role-name" />
          </div>
          <div className="field">
            <Label htmlFor="role-desc">What this role is for</Label>
            <Textarea id="role-desc" rows={2} value={editing.description}
              placeholder="Runs the diary and attendance for one batch"
              onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="role-description" />
          </div>

          <div className="field">
            <Label htmlFor="role-base">Based on *</Label>
            {editing.id ? (
              <Input value={ROLE_LABELS[editing.base_role] || editing.base_role} disabled className="bg-muted" />
            ) : (
              <select id="role-base" className="form-select" value={editing.base_role}
                onChange={(e) => setEditing({ ...editing, base_role: e.target.value, modules: [] })}
                data-testid="role-base">
                {options.map((b) => (
                  <option key={b.key} value={b.key}>{ROLE_LABELS[b.key] || b.key}</option>
                ))}
              </select>
            )}
            <p className="field-hint">
              {editing.id
                ? 'The base role is fixed once a role exists — the people holding it were given its permissions.'
                : 'A role can only ever narrow what its base role does. Pick the closest one.'}
            </p>
          </div>

          <div className="field field-wide">
            <Label data-find="Can reach">Can reach</Label>
            <div className="rounded-md border border-border divide-y divide-border" data-testid="role-modules">
              {offered.map((m) => (
                <label key={m.key} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-muted/40">
                  <Checkbox checked={editing.modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)}
                    data-testid={`role-module-${m.key}`} />
                  <span>{m.label}</span>
                </label>
              ))}
            </div>
            <p className="field-hint">
              {editing.modules.length === 0
                ? 'Nothing ticked means the role is not narrowed at all — it behaves exactly like its base role.'
                : `${editing.modules.length} of ${offered.length}. Everything else is closed, on the server as well as in the menu.`}
            </p>
          </div>

          {['instructor', 'tenant_admin'].includes(editing.base_role) && (
            <div className="field field-wide">
              <Label data-find="Limited to batches">Limited to these batches</Label>
              {batches.length === 0 ? (
                <p className="field-hint" data-testid="no-batches">No batches yet — create one under Batches first.</p>
              ) : (
                <div className="rounded-md border border-border divide-y divide-border max-h-56 overflow-y-auto" data-testid="role-batches">
                  {batches.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-muted/40">
                      <Checkbox checked={editing.batch_ids.includes(b.id)} onCheckedChange={() => toggleBatch(b.id)}
                        data-testid={`role-batch-${b.id}`} />
                      <span className="min-w-0 truncate">{b.name}{b.code ? ` · ${b.code}` : ''}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="field-hint">
                {editing.batch_ids.length === 0
                  ? 'None ticked means whatever their base role would normally see.'
                  : `Only these ${editing.batch_ids.length} batch${editing.batch_ids.length === 1 ? '' : 'es'} — their students, their attendance, their diaries.`}
              </p>
            </div>
          )}
        </>
      ),
    });
    side = {
      open: true, onOpenChange: () => setEditing(null),
      title: editing.id ? `Edit: ${editing.name || 'role'}` : 'New role',
      subtitle: 'What this role may reach', icon: IdentificationBadge,
      children: f.body, footer: f.footer, testid: 'role-pane',
    };
  }

  const columns = [
    { key: 'name', header: 'Role', mobilePrimary: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.name}</div>
          {r.description && <div className="text-[11px] text-muted-foreground truncate">{r.description}</div>}
        </div>
      ) },
    { key: 'base_role', header: 'Based on', render: (r) => ROLE_LABELS[r.base_role] || r.base_role },
    { key: 'modules', header: 'Can reach',
      accessor: (r) => (r.modules || []).length,
      render: (r) => (!r.modules || r.modules.length === 0
        ? <span className="text-muted-foreground">Everything its base role can</span>
        : <span className="text-[12px]">{r.modules.map((m) => moduleLabels[m] || m).join(', ')}</span>) },
    { key: 'batch_ids', header: 'Batches',
      accessor: (r) => (r.batch_ids || []).length,
      render: (r) => (!r.batch_ids || r.batch_ids.length === 0
        ? <span className="text-muted-foreground">All</span>
        : <span className="inline-flex items-center gap-1 text-[12px]"><UsersThree className="w-3.5 h-3.5" />{r.batch_ids.length}</span>) },
    { key: 'user_count', header: 'People', align: 'right',
      render: (r) => <span className="tabular-nums">{r.user_count ?? 0}</span> },
  ];

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
        <div className="p-4 lg:p-5">
          <DataGrid
            title="Roles"
            rows={roles}
            columns={columns}
            loading={loading}
            onRefresh={load}
            searchKeys={['name', 'description']}
            emptyMessage="No roles yet. Create one to give somebody a narrower view than their built-in role."
            onRowDoubleClick={(r) => setEditing({ ...BLANK, ...r, modules: r.modules || [], batch_ids: r.batch_ids || [] })}
            primaryActions={(
              <Button size="sm" className="h-9" onClick={() => setEditing({ ...BLANK })} data-testid="new-role-btn">
                <Plus className="w-4 h-4" /> New role
              </Button>
            )}
            selectionActions={[
              { key: 'edit', label: 'Edit', icon: PencilSimple,
                onClick: ([r]) => setEditing({ ...BLANK, ...r, modules: r.modules || [], batch_ids: r.batch_ids || [] }) },
              { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([r]) => remove(r) },
            ]}
            data-testid="roles-grid"
          />
        </div>
      </SplitLayout>
    </DashboardLayout>
  );
}
